/**
 * The medication catalog, as the add-medication form uses it.
 *
 * Two reads, and they do different jobs:
 *
 * - `useMedicationProductVariations` answers "what variations of this product
 *   exist in this country?" while the patient types the product name. Its
 *   `dimensions` are what the dose-form, strength, container and pack-size
 *   sections offer. The `packs` it also returns are deliberately not used: the
 *   patient never picks a pack, and the pack is resolved server-side from the
 *   field values she ends up with.
 * - `useResolveMedicationPackCode` answers "which pack is this scanned code?"
 *   so a scan can fill section 1 in one go.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as ImagePicker from "expo-image-picker";
import { useQuery } from "@tanstack/react-query";

import { ApiRequestError, throwApiError } from "@src/packages/api/api-error";
import { useApiClient } from "@src/packages/api/louise-api";

import { preparePackPhoto } from "./photoCapture";

import type {
  MedicationCatalogProduct,
  MedicationQuickAddItem,
  MedicationCodeResolution,
} from "./types";

// The name lookup and its deadline stay where #3825 put them: the document
// vault resolves a medication from extracted text through this same module,
// and that read is not the add-medication form's.
export {
  findMedicationByName,
  resolveMedicationCatalogId,
} from "./catalog-match";
export type { MedicationCatalogItem } from "./catalog-match";

const PRODUCT_QUERY_MIN_LENGTH = 2;
const PRODUCT_QUERY_LIMIT = 10;
const PRODUCT_QUERY_DEBOUNCE_MS = 300;

export const medicationCatalogKeys = {
  all: ["medication-catalog"] as const,
  products: (patientId: string, countryCode: string, query: string) =>
    [
      ...medicationCatalogKeys.all,
      "products",
      patientId,
      countryCode,
      query,
    ] as const,
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Products matching what the patient has typed, in her own country, each with
 * the dose forms, strengths, containers and pack sizes that exist for it.
 */
export function useMedicationProductVariations(
  query: string,
  countryCode: string | null,
) {
  const { client, patientId } = useApiClient();
  const debouncedQuery = useDebouncedValue(
    query.trim(),
    PRODUCT_QUERY_DEBOUNCE_MS,
  );
  // A missing country does not disable the lookup. The client never guesses
  // one; the server resolves the market from the patient's record or the
  // request, and says so when it cannot.
  const enabled =
    debouncedQuery.length >= PRODUCT_QUERY_MIN_LENGTH && !!patientId;

  const result = useQuery({
    queryKey: medicationCatalogKeys.products(
      patientId ?? "",
      countryCode ?? "",
      debouncedQuery,
    ),
    queryFn: async ({ signal }): Promise<MedicationCatalogProduct[]> => {
      if (!patientId) return [];

      // Typing moves the query on, and the request for what she typed two
      // letters ago is of no use to anyone: it is cancelled rather than left
      // to finish, so a slow early answer cannot land after a fast later one.
      const response = await client.GET("/medication-catalog", {
        signal,
        params: {
          query: {
            patientId,
            q: debouncedQuery,
            // Her market when the record states one; the catalogue resolves
            // it from her address or the request when it does not.
            ...(countryCode ? { market: countryCode } : {}),
            limit: PRODUCT_QUERY_LIMIT,
          },
        },
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to look up medication products",
          "medication-catalog",
          response.response.status,
        );
      }

      return response.data?.data?.items ?? [];
    },
    enabled,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 30,
    retry: 1,
  });

  return {
    ...result,
    /**
     * Typing, with the request not yet made.
     *
     * The gap between a keystroke and the debounce firing is not an answer:
     * the query is idle, so `isFetching` is false, and a caller that trusted
     * it would say "nothing found" about a search it has not run. A caller
     * showing a spinner wants this as well, or the spinner blinks off between
     * every letter.
     */
    isSettling: query.trim() !== debouncedQuery,
  };
}

/**
 * The medications the catalogue suggests adding first, for one market.
 *
 * `market` is required by the endpoint, so unlike every other catalogue call
 * this one cannot be made without a country. A patient whose record states
 * none falls back to US here rather than losing the chips entirely.
 */
export function useMedicationQuickAdd(countryCode: string | null) {
  const { client, patientId } = useApiClient();
  const market = countryCode ?? "US";

  return useQuery({
    queryKey: [...medicationCatalogKeys.all, "quick-add", market, patientId],
    queryFn: async ({ signal }): Promise<MedicationQuickAddItem[]> => {
      const response = await client.GET("/medication-catalog/quick-add", {
        signal,
        params: {
          query: { market, ...(patientId ? { patientId } : {}) },
        },
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to load the quick-add medications",
          "medication-catalog",
          response.response.status,
        );
      }

      return response.data?.data?.items ?? [];
    },
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    retry: 1,
  });
}

/** Why a photo of a pack could not be turned into a code. */
export type PackPhotoFailure =
  "unreadable" | "too_large" | "unsupported" | "unreachable" | "failed";

/** Which part of the gesture gave up, for a failure nobody can reproduce. */
export type PackPhotoStage = "camera" | "prepare" | "request";

export class MedicationPackPhotoError extends Error {
  constructor(
    readonly reason: PackPhotoFailure,
    /**
     * What actually went wrong, carried verbatim. A phone cannot be opened in
     * a debugger, so the one chance to learn why a scan failed is the error
     * itself; the UI shows this only in a development build.
     */
    readonly detail?: string,
    readonly stage?: PackPhotoStage,
  ) {
    super(`Pack photo could not be read: ${reason}`);
    this.name = "MedicationPackPhotoError";
  }
}

/** Everything about a thrown value that is worth reading back off a phone. */
export function describeError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const status =
      error.status === undefined ? "no status" : `HTTP ${error.status}`;
    const cause =
      error.cause instanceof Error
        ? ` <- ${error.cause.name}: ${error.cause.message}`
        : "";
    return `${error.name} (${status}): ${error.message}${cause}`;
  }
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return String(error);
}

/**
 * Photograph a pack and get back what its barcode resolves to.
 *
 * This owns the whole gesture — opening the camera, shrinking and re-encoding
 * what comes back, and asking the catalog — so that tapping "scan" can open
 * the camera on the spot instead of unfolding a panel that holds a button that
 * opens the camera. Returns null when she backs out of the camera without
 * taking anything, which is not a failure and should leave the form alone.
 */
export function usePackPhotoScan() {
  const resolvePackPhoto = useResolveMedicationPackPhoto();
  const scanningRef = useRef(false);
  const [isScanning, setIsScanning] = useState(false);
  // A one-line account of what the last gesture actually did, kept only in a
  // development build. The failure that sends nothing to the server and the
  // one that sends a photo nobody can read look identical on screen; this is
  // the difference, and a phone has no console to read it from.
  const [scanTrace, setScanTrace] = useState<string | null>(null);
  const trace = useCallback((line: string) => {
    if (__DEV__) setScanTrace(line);
  }, []);

  const scanWithPhoto = useCallback(
    async (
      countryCode: string | null,
    ): Promise<MedicationCodeResolution | null> => {
      if (scanningRef.current) return null;
      setScanTrace(null);

      // Busy from the tap, not from the moment the photo lands. Opening the
      // camera can itself fail, and when it did the caller showed an error
      // with no sign anything had ever been in progress; covering the whole
      // gesture means the spinner is already up when the camera hands back.
      scanningRef.current = true;
      setIsScanning(true);
      // Each stage is named as it runs, so a failure says which part gave up
      // rather than leaving three candidates and no way to tell them apart.
      let stage: PackPhotoStage = "camera";
      try {
        const picked = await ImagePicker.launchCameraAsync({
          mediaTypes: ["images"],
          cameraType: ImagePicker.CameraType.back,
          quality: 0.7,
        });
        if (picked.canceled || !picked.assets?.length) {
          // Backing out of the camera is not a failure, so nothing is said on
          // screen — but it is indistinguishable from a camera that handed
          // back nothing, which is, so the two are named apart here.
          trace(picked.canceled ? "camera: cancelled" : "camera: no photo");
          return null;
        }

        stage = "prepare";
        const asset = picked.assets[0]!;
        trace(
          `camera: ${asset.uri.slice(0, 32)} ${asset.width}x${asset.height}`,
        );
        const { blob, fileName } = await preparePackPhoto(
          asset.uri,
          asset.fileName || "pack.jpg",
        );

        stage = "request";
        trace(`prepare: ${fileName} ${blob.type || "no type"} ${blob.size}B`);
        const resolution = await resolvePackPhoto(blob, fileName, countryCode);
        trace(`request: ${resolution.resolution} ${resolution.normalizedCode}`);
        return resolution;
      } catch (error) {
        const detail = describeError(error);
        trace(`${stage} failed: ${detail}`);
        if (error instanceof MedicationPackPhotoError) {
          throw new MedicationPackPhotoError(
            error.reason,
            error.detail ?? detail,
            error.stage ?? stage,
          );
        }
        // A request that never completed and a server that answered are not
        // the same problem, and the difference is what tells her whether to
        // move the phone or try again later. An ApiRequestError carries a
        // status only when something answered.
        const unreachable =
          error instanceof ApiRequestError && error.status === undefined;
        throw new MedicationPackPhotoError(
          unreachable ? "unreachable" : "failed",
          detail,
          stage,
        );
      } finally {
        scanningRef.current = false;
        setIsScanning(false);
      }
    },
    [resolvePackPhoto, trace],
  );

  return { scanWithPhoto, isScanning, scanTrace };
}

/**
 * Reads a pack barcode out of a photograph.
 *
 * The image is posted straight to the catalog — deliberately not through the
 * document upload pipeline, which is asynchronous and durable, and this is
 * neither: the server decodes the barcode in memory, answers, and keeps
 * nothing. A photograph of a patient's medication is not worth storing to read
 * a number off it.
 *
 * The answer is the same shape a typed code gives back, so everything
 * downstream of it is the one code path.
 */
export function useResolveMedicationPackPhoto() {
  const { client, patientId } = useApiClient();

  return useCallback(
    async (
      photo: Blob,
      fileName: string,
      countryCode: string | null,
    ): Promise<MedicationCodeResolution> => {
      if (!patientId) throw new Error("Missing patient ID");

      const response = await client.POST("/medication-catalog/resolve-code", {
        params: { query: { patientId } },
        // The generated type renders the binary field as a string, which no
        // file is, so the declared body carries the file's name and the
        // serializer below puts the bytes on the wire. Serializing is also
        // the only way to send multipart at all here: left alone,
        // openapi-fetch JSON-encodes the body and the boundary the server
        // needs to find the file never exists.
        body: { image: fileName, ...(countryCode ? { countryCode } : {}) },
        bodySerializer: (body) => {
          const form = new FormData();
          form.append("image", photo, fileName);
          if (body?.countryCode) form.append("countryCode", body.countryCode);
          return form;
        },
      });

      if (response.error) {
        const status = response.response.status;
        // Each of these is a different thing for her to do about it, so they
        // are told apart rather than collapsed into one failure. 422 covers
        // every "nothing usable here" case — no barcode, a reader that gave
        // up, and a barcode that is not a pack code (a lot label) — so its
        // copy asks for the box's own barcode rather than assuming she simply
        // missed. 415 covers a format the server will not take and an image
        // whose pixels will not decode; 413 is the 8 MB ceiling, which the
        // re-encode in preparePackPhoto keeps us well under. A 400 or 403 is
        // ours to fix, not hers to act on, and falls through to the generic
        // failure below.
        const detail = `HTTP ${status} ${JSON.stringify(response.error).slice(0, 300)}`;
        if (status === 422) {
          throw new MedicationPackPhotoError("unreadable", detail, "request");
        }
        if (status === 413) {
          throw new MedicationPackPhotoError("too_large", detail, "request");
        }
        if (status === 415) {
          throw new MedicationPackPhotoError("unsupported", detail, "request");
        }
        throwApiError(
          response.error,
          "Failed to read the pack photo",
          "medication-catalog",
          status,
        );
      }

      const resolution = response.data?.data;
      if (!resolution) throw new MedicationPackPhotoError("failed");
      return resolution;
    },
    [client, patientId],
  );
}

/**
 * Resolves one scanned pack code. The server normalizes GS1 element strings,
 * EAN-13 and the French CIP13/CIP7 forms, so the raw scan goes over as-is and
 * no `system` is guessed here.
 */
export function useResolveMedicationPackCode() {
  const { client, patientId } = useApiClient();

  return useCallback(
    async (
      code: string,
      countryCode: string | null,
    ): Promise<MedicationCodeResolution> => {
      if (!patientId) throw new Error("Missing patient ID");

      const response = await client.GET("/medication-catalog/resolve-code", {
        params: {
          // A pharmacy code usually names its own market — a CIP is French,
          // an NDC is American — so the server can place it without being
          // told, and is better placed to than this client is.
          query: {
            patientId,
            code,
            ...(countryCode ? { countryCode } : {}),
          },
        },
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to read the scanned pack code",
          "medication-catalog",
          response.response.status,
        );
      }

      const resolution = response.data?.data;
      if (!resolution) throw new Error("Empty pack code resolution");
      return resolution;
    },
    [client, patientId],
  );
}
